import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import {
  LayoutDashboard, Users, FileText, Scissors, Bell, Settings, LogOut, PlusCircle, BarChart3, MessageCircle, Menu, X, UserCheck
} from "lucide-react";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import api from "../lib/api";

const LOGO_URL = "/logo.jpg";

const navItems = [
  { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { path: "/customers", icon: Users, label: "Customers" },
  { path: "/inquiries", icon: UserCheck, label: "Requested Customers", badgeKey: "inquiries" },
  { path: "/slips", icon: FileText, label: "Slips" },
  { path: "/workers", icon: Scissors, label: "Workers" },
  { path: "/whatsapp", icon: MessageCircle, label: "WhatsApp" },
  { path: "/analytics", icon: BarChart3, label: "Analytics" },
  { path: "/notifications", icon: Bell, label: "Notifications" },
  { path: "/settings", icon: Settings, label: "Settings" },
];

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [badges, setBadges] = useState({ inquiries: 0 });

  // Fetch unread inquiry count
  useEffect(() => {
    const fetchBadges = () => {
      api.get("/inquiries").then(r => {
        const newCount = r.data.filter(i => i.status === "new").length;
        setBadges({ inquiries: newCount });
      }).catch(() => {});
    };
    fetchBadges();
    const interval = setInterval(fetchBadges, 60000); // refresh every 60s
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const isActive = (path) => {
    if (path === "/dashboard") return location.pathname === "/dashboard";
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  return (
    <>
      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 h-14 bg-[hsl(0,0%,5%)] border-b border-border flex items-center justify-between px-4 z-50">
        <div 
          className="flex items-center gap-3 cursor-pointer" 
          onClick={() => navigate("/dashboard")}
        >
          <img src={LOGO_URL} alt="VR" className="w-8 h-8 rounded-full object-cover border border-[rgba(212,175,55,0.3)]" />
          <span className="font-serif text-sm font-semibold text-foreground">VR Darzi</span>
        </div>
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setIsOpen(true)}
          className="p-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <Menu className="w-6 h-6" />
        </button>
      </header>

      {/* Mobile Overlay */}
      {isOpen && (
        <div className="md:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={() => setIsOpen(false)} />
      )}

      {/* Mobile Sidebar */}
      <aside className={`md:hidden fixed top-0 right-0 h-screen w-72 bg-[hsl(0,0%,5%)] border-l border-border flex flex-col z-50 transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="p-4 flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer" 
            onClick={() => handleNavClick("/dashboard")}
          >
            <img src={LOGO_URL} alt="VR" className="w-10 h-10 rounded-full object-cover border border-[rgba(212,175,55,0.3)]" />
            <div>
              <h1 className="font-serif text-base font-semibold text-foreground leading-tight">VR The Darzi</h1>
              <p className="text-[10px] uppercase tracking-[0.25em] gold-text font-medium">Studio</p>
            </div>
          </div>
          <button onClick={() => setIsOpen(false)} className="p-2 text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>
        <Separator className="opacity-30" />
        <div className="px-4 py-4">
          <Button onClick={() => handleNavClick("/slips/new")} className="w-full btn-maroon rounded-sm text-sm gap-2">
            <PlusCircle className="w-4 h-4" /> New Slip
          </Button>
        </div>
        <Separator className="opacity-30" />
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const badgeCount = item.badgeKey ? badges[item.badgeKey] : 0;
            return (
              <button
                key={item.path}
                onClick={() => handleNavClick(item.path)}
                className={`sidebar-link w-full flex items-center gap-3 px-3 py-3 text-sm rounded-sm transition-all ${isActive(item.path) ? "active text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium flex-1 text-left">{item.label}</span>
                {badgeCount > 0 && (
                  <span className="bg-[#722F37] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                    {badgeCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
        <Separator className="opacity-30" />
        <div className="p-4">
          <button onClick={() => { logout(); navigate("/login"); }} className="w-full flex items-center gap-3 px-3 py-3 text-sm text-muted-foreground hover:text-red-400 rounded-sm">
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed top-0 left-0 h-screen w-64 bg-[hsl(0,0%,5%)] border-r border-border flex-col z-50">
        <div 
          className="p-6 flex items-center gap-3 cursor-pointer hover:opacity-90 transition-opacity" 
          onClick={() => handleNavClick("/dashboard")}
        >
          <img src={LOGO_URL} alt="VR" className="w-10 h-10 rounded-full object-cover border border-[rgba(212,175,55,0.3)]" />
          <div>
            <h1 className="font-serif text-base font-semibold text-foreground leading-tight">VR The Darzi</h1>
            <p className="text-[10px] uppercase tracking-[0.25em] gold-text font-medium">Studio</p>
          </div>
        </div>
        <Separator className="opacity-30" />
        <div className="px-4 py-4">
          <Button data-testid="new-slip-btn" onClick={() => handleNavClick("/slips/new")} className="w-full btn-maroon rounded-sm text-sm gap-2">
            <PlusCircle className="w-4 h-4" /> New Slip
          </Button>
        </div>
        <Separator className="opacity-30" />
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const badgeCount = item.badgeKey ? badges[item.badgeKey] : 0;
            return (
              <button
                key={item.path}
                data-testid={`nav-${item.label.toLowerCase()}`}
                onClick={() => handleNavClick(item.path)}
                className={`sidebar-link w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-all ${isActive(item.path) ? "active text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                <item.icon className="w-4 h-4 flex-shrink-0" />
                <span className="font-medium flex-1 text-left">{item.label}</span>
                {badgeCount > 0 && (
                  <span className="bg-[#722F37] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center leading-none">
                    {badgeCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
        <Separator className="opacity-30" />
        <div className="p-4">
          <button data-testid="logout-btn" onClick={() => { logout(); navigate("/login"); }} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-muted-foreground hover:text-red-400 rounded-sm">
            <LogOut className="w-4 h-4" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
}
